import * as Sentry from '@sentry/browser';
import { Transaction } from '@sentry/types';
import { UuidHelper } from '@/helpers/uuid-helper';

// NOTE Check performance-guide.md if you just start working on that.

export class SentryPerformanceTracker {
	transactions: Transaction[] = [];

	// We need to keep names with hash to differentiate overlapped events.
	markNamesWithHash: Record<string, string> = {};
	markActions: boolean;

	constructor(props: { markActions?: boolean }) {
		this.markActions = props.markActions || false;
	}
	startTransaction(transactionName: string): void {
		this.markTransactionStart(transactionName);
		this.transactions.push(Sentry.startTransaction({ name: transactionName, trimEnd: true }));
	}
	finishTransaction(transactionName: string): void {
		this.markTransactionEnd(transactionName);
		const transaction = this.getTransactionByName(transactionName);
		if (!transaction) return;

		transaction.finish();

		this.transactions = this.transactions.filter(transaction => transaction.name !== transactionName);
	}

	addChild(transactionName: string, description: string = ''): () => void {
		const transaction = this.getTransactionByName(transactionName);
		if (!transaction) return () => {};
		const span = transaction.startChild({
			op: 'task',
			description,
		});
		return () => span.finish();
	}

	getTransactionByName(transactionName: string): Transaction | undefined {
		return this.transactions.find(transaction => transaction.name === transactionName);
	}

	// Marks are displayed in performance tab of chrome devtools.
	// There you can ensure you're measuring correct things.
	markTransactionStart(name: string): void {
		if (!this.markActions) return;
		const nameWithHash = `${name}-${UuidHelper.generateUUID(3)}`;
		if (this.markNamesWithHash[name]) return;
		this.markNamesWithHash[name] = nameWithHash;
		performance.mark(`${nameWithHash} start`);
	}
	markTransactionEnd(name: string): void {
		if (!this.markActions) return;
		const nameWithHash = this.markNamesWithHash[name];
		if (!nameWithHash) return;
		performance.mark(`${nameWithHash} end`);
		performance.measure(`${name}`, `${nameWithHash} start`, `${nameWithHash} end`);
		performance.clearMarks(`${nameWithHash} start`);
		performance.clearMarks(`${nameWithHash} end`);
		performance.clearMeasures(`${name}`);
		delete this.markNamesWithHash[name];
	}
}

export const sentryPerformanceTracker = new SentryPerformanceTracker({
	markActions: process.env.NODE_ENV === 'development',
});

// ######## See sentry.md for explanation. ########

// These transactions are intentionally limited and intended to track performance when switching from one tab to another.
// If tab is loaded on startup (navigating by url) - event won't trigger.
// Even limited events should be useful to track performance improvement/regression.
export enum PageNavigationTransaction {
	Home = 'page-navigation-home',
	New = 'page-navigation-new',
	Popular = 'page-navigation-popular',
	Watchlist = 'page-navigation-watchlist',
}

export enum StartupTransaction {
	Full = 'full-app-startup',
	Frontend = 'frontend-app-startup',
	Android = 'android-app-startup', // Used only on android side, but keeping here as docs.
}
